import React from 'react';

// Rate manual: рендер таблиц факторов из options JSON (mercantilebrace)
// Дизайн повторяет ReportMercantlePolunio.cfm (innovare-lucee)

const GREEN = '#E2EFDA';
const BLUE = '#DDEBF7';

const thStyle = (bg) => ({
  border: '1px solid #000',
  padding: '3px 8px',
  textAlign: 'center',
  fontWeight: 'bold',
  background: bg || GREEN
});
const tdStyle = {
  border: '1px solid #000',
  padding: '3px 8px',
  textAlign: 'center'
};
const tdLeft = { ...tdStyle, textAlign: 'left' };

// Подсветка опции, откуда взята расценка (клик по "?" в результатах)
const HL_BG = '#fff3b0';
const HL_BORDER = '#f0ad4e';
const hlStyle = (base, key, match) =>
  (match && key && match(key))
    ? { ...base, background: HL_BG, boxShadow: `inset 0 0 0 2px ${HL_BORDER}` }
    : base;
const SCROLL_MARGIN = { scrollMarginTop: '80px' };

const CT_COLUMNS = [
  { key: 'Frame', ref: 'O' },
  { key: 'JM', ref: 'NC' },
  { key: 'M-NC', ref: null },
  { key: 'F-R', ref: 'MFR' }
];

// Выбор данных таблицы по штату (аналог checkPolunioStructState)
const pickState = (obj, state) => {
  if (!obj || typeof obj !== 'object') return {};
  if (obj[state]) return obj[state];
  if (obj._all_) return obj._all_;
  return {};
};

// 0.9 -> "10% Credit", 1.2 -> "20% Debit", 1 -> "As Above"
const pct = (n) => {
  const r = Math.round(n * 100) / 100;
  return String(r);
};
const creditDebit = (mult) => {
  const m = parseFloat(mult);
  if (isNaN(m)) return String(mult);
  if (m < 1) return `${pct((1 - m) * 100)}% Credit`;
  if (m > 1) return `${pct((m - 1) * 100)}% Debit`;
  return 'As Above';
};

const rangeStart = (key) => {
  const m = String(key).replace('range_', '');
  return parseFloat(m.split('-')[0]) || 0;
};

const Section = ({ title, anchor, match, children }) => {
  // Подсветка секции целиком (для полей, влияющих на таблицу без конкретной ячейки)
  const secKey = anchor ? 'section|' + anchor : undefined;
  const on = secKey && match && match(secKey);
  return (
    <div
      id={anchor ? 'mbrt-' + anchor : undefined}
      data-mbkey={secKey}
      style={{
        marginBottom: '22px',
        ...SCROLL_MARGIN,
        ...(on ? { boxShadow: `0 0 0 3px ${HL_BORDER}`, borderRadius: '4px', padding: '6px' } : null)
      }}
    >
      <div style={{ fontWeight: 'bold', textDecoration: 'underline', marginBottom: '6px', ...(on ? { background: HL_BG } : null) }}>{title}</div>
      {children}
    </div>
  );
};

// Простая таблица «label -> кредит/дебет» (с подсветкой строки по r.mbkey)
const MultTable = ({ leftHeader, rightHeader, rows, headerBg, note, highlightMatch }) => (
  <table style={{ borderCollapse: 'collapse', minWidth: '320px' }}>
    <thead>
      <tr>
        <th style={thStyle(headerBg)}>{leftHeader}</th>
        <th style={thStyle(headerBg)}>{rightHeader}</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={i} data-mbkey={r.mbkey || undefined} style={SCROLL_MARGIN}>
          <td style={hlStyle(tdLeft, r.mbkey, highlightMatch)}>{r.label}</td>
          <td style={hlStyle(tdStyle, r.mbkey, highlightMatch)}>{r.display}</td>
        </tr>
      ))}
      {note && (
        <tr>
          <td style={tdStyle} colSpan="2">{note}</td>
        </tr>
      )}
    </tbody>
  </table>
);

const MercantileBraceRateTables = ({ settings, state, highlightMatch }) => {
  const lp = settings?.lineProperty;
  if (!lp) {
    return <p className="text-muted">No lineProperty data in settings.</p>;
  }

  // --- 1. Minimum Property Rates Excluding Wind (NonWindBaseRate) ---
  const nwbr = pickState(lp.NonWindBaseRate, state);
  const occupancies = Object.keys(nwbr).filter(k => !k.startsWith('__'));

  const nwbrTable = (
    <Section title={`MINIMUM PROPERTY RATES EXCLUDING WIND (${state})`} anchor="NonWindBaseRate" match={highlightMatch}>
      <table style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle(GREEN)}>OCCUPANCY</th>
            <th style={thStyle(GREEN)} colSpan={CT_COLUMNS.length}>CONSTRUCTION</th>
          </tr>
          <tr>
            <th style={thStyle(GREEN)}></th>
            {CT_COLUMNS.map(ct => (
              <th key={ct.key} style={thStyle(GREEN)}>
                {ct.key}{ct.ref ? ` (${ct.ref})` : ''}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {occupancies.map(occ => (
            <tr key={occ}>
              <td style={tdLeft}>{occ}</td>
              {CT_COLUMNS.map(ct => {
                const key = `NonWindBaseRate|${occ}|${ct.key}`;
                return (
                  <td key={ct.key} data-mbkey={key} style={hlStyle(tdStyle, key, highlightMatch)}>
                    {nwbr[occ]?.[ct.key] !== undefined ? nwbr[occ][ct.key] : '—'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );

  // --- 2. Protection Class ---
  const pc = pickState(lp.protectionClass, state);
  const pcRows = Object.keys(pc)
    .sort((a, b) => parseFloat(a) - parseFloat(b))
    .map(k => ({ label: k, display: creditDebit(pc[k]), mbkey: `protectionClass|${k}` }));

  // --- 3. Basic Form (Cause of Loss) ---
  const col = pickState(lp.CauseOfLossCredit, state);
  const basicRows = col.Basic !== undefined
    ? [{ label: 'Basic Form', display: creditDebit(col.Basic), mbkey: 'CauseOfLossCredit|Basic' }]
    : [];

  // --- 4. Age of Building ---
  const age = pickState(lp.AgeOfBuildingCredits, state);
  const ageRows = Object.keys(age)
    .sort((a, b) => rangeStart(a) - rangeStart(b))
    .map(k => ({ label: `${k.replace('range_', '')} Years`, display: creditDebit(age[k]), mbkey: `AgeOfBuildingCredits|${k}` }));

  // --- 5. AOP Deductible ---
  const aop = pickState(lp.PropDeductible, state);
  const aopRows = Object.keys(aop)
    .sort((a, b) => parseFloat(b) - parseFloat(a))
    .map(k => ({ label: `USD ${parseInt(k).toLocaleString('en-US')}`, display: creditDebit(aop[k]), mbkey: `PropDeductible|${k}` }));

  // --- 6. Named Storm Deductible ---
  const nsd = pickState(lp.WindDedOptionDebit, state);
  let namedStorm = 'N/A';
  if (nsd && nsd['Named Windstorm'] !== undefined) {
    namedStorm = creditDebit(nsd['Named Windstorm']);
  }

  // --- 7. Private Protections + Risk Factors ---
  const ppc = pickState(lp.PrivateProtectionsCredits, state);
  const highCrime = pickState(lp.HighCrimeDebit, state);
  const lossFree = pickState(lp.LossFreeCredit, state);
  const fullyUpd = pickState(lp.fullyUpdatedProperty10Years, state);

  const ppLabels = {
    AlarmOrSecurity: 'Central Station Burglar Alarm',
    FullySprinklered: 'Fully Sprinklered',
    AAM: 'Central Station Burglar Alarm',
    AF: 'Central Station Fire Alarm',
    AS: 'Fully Sprinklered Alarm'
  };
  const privateRows = Object.keys(ppc).map(k => ({
    label: ppLabels[k] || k,
    display: creditDebit(ppc[k]),
    mbkey: `PrivateProtectionsCredits|${k}`
  }));

  const riskRows = [];
  if (highCrime['1'] !== undefined) {
    riskRows.push({ label: 'High Crime / Poor Area', display: creditDebit(highCrime['1']), mbkey: 'HighCrimeDebit|1' });
  }
  if (fullyUpd['1'] !== undefined) {
    riskRows.push({ label: 'Fully Updated with 10 Years', display: creditDebit(fullyUpd['1']), mbkey: 'fullyUpdatedProperty10Years|1' });
  }
  if (lossFree['range_5-999'] !== undefined) {
    const maxCredit = pct((1 - parseFloat(lossFree['range_5-999'])) * 100);
    let perYear = '';
    if (lossFree['1'] !== undefined && lossFree['2'] !== undefined) {
      perYear = ` (or ${pct((parseFloat(lossFree['1']) - parseFloat(lossFree['2'])) * 100)}% per year, each year loss free)`;
    }
    riskRows.push({ label: 'Loss Free 5 Years (Verified)', display: `${maxCredit}% Max Credit${perYear}`, mbkey: 'LossFreeCredit' });
  }

  const ppRowCount = Math.max(privateRows.length, riskRows.length);
  const privateRiskTable = (privateRows.length > 0 || riskRows.length > 0) && (
    <Section title="PRIVATE PROTECTIONS & RISK FACTORS" anchor="PrivateProtectionsCredits" match={highlightMatch}>
      <table style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle(GREEN)}>Private Protections</th>
            <th style={thStyle(GREEN)}>Credit</th>
            <th style={thStyle(GREEN)}>Risk Factors</th>
            <th style={thStyle(GREEN)}>Credit/Debit</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: ppRowCount }).map((_, i) => {
            const pRow = privateRows[i];
            const rRow = riskRows[i];
            return (
              <tr key={i} style={SCROLL_MARGIN}>
                <td data-mbkey={pRow?.mbkey || undefined} style={hlStyle(tdLeft, pRow?.mbkey, highlightMatch)}>{pRow?.label || ''}</td>
                <td style={hlStyle(tdStyle, pRow?.mbkey, highlightMatch)}>{pRow?.display || ''}</td>
                <td data-mbkey={rRow?.mbkey || undefined} style={hlStyle(tdLeft, rRow?.mbkey, highlightMatch)}>{rRow?.label || ''}</td>
                <td style={hlStyle(tdStyle, rRow?.mbkey, highlightMatch)}>{rRow?.display || ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Section>
  );

  // --- 8. Additional Wind Rate (WindBaseRate) по tier ---
  const wbr = pickState(lp.WindBaseRate, state);
  const tierLabels = {
    tier1: 'Within 10 Miles of Coast / Coastal County (Tier 1)',
    tier2: 'Second Tier Counties (Tier 2)',
    tier3: 'Other Counties (Tier 3)',
    tricounty: 'Tri-County'
  };
  const tierKeys = Object.keys(wbr).filter(k => tierLabels[k]);

  const renderWindTier = (tierKey) => {
    const tier = wbr[tierKey];
    if (!tier || typeof tier !== 'object') return null;
    const ctKeys = Object.keys(tier).filter(k => !k.startsWith('__'));
    // собрать список distance-ключей по всем конструкциям
    const dtcSet = [];
    ctKeys.forEach(ct => {
      Object.keys(tier[ct] || {}).forEach(dtc => {
        if (!dtcSet.includes(dtc)) dtcSet.push(dtc);
      });
    });
    const cols = CT_COLUMNS.filter(c => ctKeys.includes(c.key));
    const cell = (ct, dtc) => {
      const v = tier[ct]?.[dtc];
      if (!v) return '—';
      if (v.Rate !== undefined) {
        const back = v.RateBackYard !== undefined && v.RateBackYard !== v.Rate
          ? ` (BY ${v.RateBackYard})` : '';
        return `${v.Rate}${back}`;
      }
      // Защита: вложенность по году постройки {range_YYYY-YYYY:{Rate}} — берём новейший диапазон
      const rk = Object.keys(v).filter(k => k.startsWith('range_'));
      if (rk.length) {
        const newest = rk.sort((a, b) => rangeStart(a) - rangeStart(b)).pop();
        const rv = v[newest];
        if (rv && rv.Rate !== undefined) return String(rv.Rate);
      }
      return '—';
    };
    return (
      <div key={tierKey} style={{ marginBottom: '12px' }}>
        <div style={{ fontWeight: '600', marginBottom: '4px' }}>{tierLabels[tierKey]}</div>
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={thStyle(BLUE)}>Distance To Coast</th>
              {cols.map(c => (
                <th key={c.key} style={thStyle(BLUE)}>{c.key}{c.ref ? ` (${c.ref})` : ''}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {dtcSet.map(dtc => (
              <tr key={dtc}>
                <td style={tdLeft}>{dtc === 'between 5 and 10 miles' ? 'Within Coastal County' : dtc}</td>
                {cols.map(c => {
                  const key = `WindBaseRate|${tierKey}|${c.key}|${dtc}`;
                  return (
                    <td key={c.key} data-mbkey={key} style={hlStyle(tdStyle, key, highlightMatch)}>
                      {cell(c.key, dtc)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Tri-County — отдельная секция, сгруппированная по диапазону "год постройки"
  // (повторяет ReportMercantlePolunio.cfm: "Year Built Post X - Y Rates")
  const TRI_DTC = [
    'Outside of wind pool but less than 5 miles',
    'between 5 and 10 miles',
    '>10 but <30 miles'
  ];
  const normRange = (rng) => (rng === 'range_2002-9999' ? 'range_2002-999999' : rng);
  const rangeLabelTri = (rng) => {
    const raw = rng.replace('range_', '');
    const [a, b] = raw.split('-');
    return `${a} - ${b}`;
  };
  const renderTriCounty = () => {
    const tri = wbr.tricounty;
    if (!tri || typeof tri !== 'object') return null;
    const ctKeys = CT_COLUMNS.map(c => c.key).filter(k => tri[k]);
    const rangeSet = [];
    ctKeys.forEach(ct => {
      Object.keys(tri[ct] || {}).forEach(dtc => {
        Object.keys(tri[ct][dtc] || {}).forEach(rng => {
          const n = normRange(rng);
          if (!rangeSet.includes(n)) rangeSet.push(n);
        });
      });
    });
    if (!rangeSet.length) return null;
    rangeSet.sort((a, b) => rangeStart(a) - rangeStart(b));
    const getRate = (ct, dtc, rng) => {
      const dtcObj = tri[ct]?.[dtc];
      if (!dtcObj) return undefined;
      const cellV = dtcObj[rng] || (rng === 'range_2002-999999' ? dtcObj['range_2002-9999'] : undefined);
      return cellV?.Rate;
    };
    return (
      <div style={{ marginTop: '14px' }}>
        <div style={{ fontWeight: 'bold', marginBottom: '6px' }}>
          AMELIA FLORIDA — TRI-COUNTY ADDITIONAL WIND RATES
        </div>
        {rangeSet.map(rng => {
          const rows = ctKeys.filter(ct => TRI_DTC.some(dtc => getRate(ct, dtc, rng) !== undefined));
          if (!rows.length) return null;
          return (
            <div key={rng} style={{ marginBottom: '12px' }}>
              <table style={{ borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th colSpan={TRI_DTC.length + 1} style={thStyle(BLUE)}>
                      Year Built Post {rangeLabelTri(rng)} Rates
                    </th>
                  </tr>
                  <tr>
                    <th style={thStyle(GREEN)}>Construction</th>
                    {TRI_DTC.map(dtc => (
                      <th key={dtc} style={thStyle(GREEN)}>{dtc}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(ct => {
                    const ref = CT_COLUMNS.find(c => c.key === ct)?.ref;
                    return (
                      <tr key={ct}>
                        <td style={tdLeft}>{ct}{ref ? ` (${ref})` : ''}</td>
                        {TRI_DTC.map(dtc => {
                          const r = getRate(ct, dtc, rng);
                          return <td key={dtc} style={tdStyle}>{r === undefined ? '' : r}</td>;
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
        <div style={{ fontWeight: 'bold', marginTop: '4px' }}>
          NO FRAME TO BE WRITTEN IN TRI-COUNTY PRIOR TO 2002
        </div>
      </div>
    );
  };

  const windSurcharge = lp.BuildingAgeWindSurcharge?._all_?.Debit;

  const windRateSection = tierKeys.length > 0 && (
    <Section title={`ADDITIONAL WIND RATE (${state})`} anchor="WindBaseRate" match={highlightMatch}>
      {tierKeys.filter(k => k !== 'tricounty').map(renderWindTier)}
      {tierKeys.includes('tricounty') && renderTriCounty()}
      {windSurcharge !== undefined && (
        <div style={{ fontWeight: 'bold', marginTop: '6px' }}>
          WIND RATES SURCHARGED {pct((parseFloat(windSurcharge) - 1) * 100)}% IF BUILT PRIOR TO 1995 (WIND ZONE 1 ONLY)
        </div>
      )}
    </Section>
  );

  // --- 9. Wind Deductible Minimum + Wind Hail Deductible ---
  const wdm = pickState(lp.windDeductibleMinimum, state);
  const whd = pickState(lp.WindHailDeductible, state);
  const wdmDTC = ['Within 10 Miles of Coast', 'Within Coastal County', 'Second Tier', 'Other Counties'];
  const wdmZones = [
    { key: 'Zone2', label: 'Zone 2 & 3 Min' },
    { key: 'Zone1', label: 'Zone 1 Min' },
    { key: 'TriCounty', label: 'TriCounty' }
  ];
  const wdmPrc = ['3%', '5%', '10%'];
  const hasWdm = wdm && Object.keys(wdm).length > 0;

  const windDedSection = hasWdm && (
    <Section title="WIND DEDUCTIBLE — MINIMUM & INCREASE CREDIT" anchor="WindHailDeductible" match={highlightMatch}>
      <table style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle(BLUE)}>Distance To Coast</th>
            {wdmZones.map(z => <th key={z.key} style={thStyle(BLUE)}>{z.label}</th>)}
            {wdmPrc.map(p => <th key={p} style={thStyle(BLUE)}>Increase Ded {p}</th>)}
          </tr>
        </thead>
        <tbody>
          {wdmDTC.map(dtc => (
            <tr key={dtc}>
              <td style={tdLeft}>{dtc}</td>
              {wdmZones.map(z => (
                <td key={z.key} style={tdStyle}>{wdm[z.key]?.[dtc] ?? '—'}</td>
              ))}
              {wdmPrc.map(p => {
                const cell = whd[p]?.[dtc];
                return (
                  <td key={p} style={tdStyle}>
                    {cell !== undefined ? `${pct((1 - parseFloat(cell)) * 100)}% Credit` : 'N/A'}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );

  // --- 10. Roof Replaced ---
  const roof = pickState(lp.Roofreplaced, state);
  const roofRows = Object.keys(roof)
    .sort((a, b) => rangeStart(a) - rangeStart(b))
    .map(k => ({ label: `Within ${String(k).split('-')[1]} Years`, display: creditDebit(roof[k]), mbkey: `Roofreplaced|${k}` }));

  // --- 11. Shutters ---
  const shutters = pickState(lp.ShuttersCredit, state);
  const shutterRows = [];
  Object.keys(shutters).forEach(zone => {
    const zoneObj = shutters[zone];
    if (zoneObj && typeof zoneObj === 'object') {
      Object.keys(zoneObj).forEach(shut => {
        const rngObj = zoneObj[shut];
        if (rngObj && typeof rngObj === 'object') {
          Object.keys(rngObj).forEach(rng => {
            shutterRows.push({
              zone,
              shutter: shut,
              years: `${String(rng).replace('range_', '').split('-')[0]} - ${String(rng).split('-')[1]} Years`,
              display: creditDebit(rngObj[rng])
            });
          });
        }
      });
    }
  });

  // --- 12. Fire Mod (Hazardous Exposures / Commercial Cooking) ---
  const fireMod = pickState(lp.FireMod_AdvFollExpBldg, state);
  const cooking = pickState(lp.Cooking_PrcBuildingInhabited, state);
  const fireModRows = Object.keys(fireMod)
    .sort()
    .map(k => ({
      label: k === '1' ? 'Hazardous exposure present' : k === '0' ? 'No hazardous exposure' : k,
      display: creditDebit(fireMod[k]),
      mbkey: `FireMod_AdvFollExpBldg|${k}`
    }));
  const cookingRows = Object.keys(cooking).map(k => ({
    label: k,
    display: creditDebit(cooking[k]),
    mbkey: `Cooking_PrcBuildingInhabited|${k}`
  }));
  const fireModSection = (fireModRows.length > 0 || cookingRows.length > 0) && (
    <Section title="FIRE MOD — HAZARDOUS EXPOSURES" anchor="FireMod" match={highlightMatch}>
      <div style={{ marginBottom: '6px', color: '#555' }}>
        Applies to the Non-Wind rate. Triggered by any hazardous exposure (Shop Carpentry, Auto Repair,
        Auto Paint &amp; Body, Welding, Manufacturing) or by commercial cooking with equipment.
      </div>
      <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
        {fireModRows.length > 0 && (
          <MultTable leftHeader="Advanced-Following Exposure" rightHeader="Credit / Debit" rows={fireModRows} headerBg={GREEN} highlightMatch={highlightMatch} />
        )}
        {cookingRows.length > 0 && (
          <MultTable leftHeader="Commercial Cooking — % Inhabited" rightHeader="Credit / Debit" rows={cookingRows} headerBg={GREEN} highlightMatch={highlightMatch} />
        )}
      </div>
    </Section>
  );

  // --- 13. Building Vacancy ---
  const vacancy = pickState(lp.BuildingVacancy, state);
  const vacancyRows = Object.keys(vacancy).map(k => ({
    label: k,
    display: creditDebit(vacancy[k]),
    mbkey: `BuildingVacancy|${k}`
  }));

  // --- 14. TRIA (Terrorism) — Property ---
  const triaProp = settings.lineTRIA?._all_?.property;
  const triaStd = triaProp?.MultipliersTRIA?.rate;
  const triaMetro = triaProp?.MultipliersTRIACountyInLocIsMetropolitan;
  const triaSection = triaStd && (
    <Section title="TRIA (TERRORISM) — PROPERTY" anchor="lineTRIA" match={highlightMatch}>
      <div style={{ marginBottom: '6px', color: '#555' }}>
        Premium = greater of (TIV × TIV&nbsp;Multiplier) vs (Property premium × AP&nbsp;Multiplier),
        minimum USD {triaStd.MinPr}.
      </div>
      <table style={{ borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={thStyle(GREEN)}>Scope</th>
            <th style={thStyle(GREEN)}>TIV Multiplier</th>
            <th style={thStyle(GREEN)}>Premium (AP) Multiplier</th>
            <th style={thStyle(GREEN)}>Min Premium</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdLeft}>Standard</td>
            <td style={tdStyle}>{triaStd.MultiplierTIV}</td>
            <td style={tdStyle}>{triaStd.MultiplerAP}</td>
            <td style={tdStyle}>USD {triaStd.MinPr}</td>
          </tr>
          {triaMetro && Object.keys(triaMetro).sort().map(cnt => (
            <tr key={cnt}>
              <td style={tdLeft}>{cnt} (metropolitan)</td>
              <td style={tdStyle}>{triaMetro[cnt].MultiplierTIV}</td>
              <td style={tdStyle}>{triaMetro[cnt].MultiplerAP}</td>
              <td style={tdStyle}>USD {triaStd.MinPr}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );

  return (
    <div style={{ fontSize: '0.9em', overflowX: 'auto' }}>
      <p style={{ margin: '0 0 14px', color: '#666' }}>
        All rates are read from options file <code>{settings.__filename || ''}</code>. Values shown for state <b>{state}</b>.
      </p>

      {nwbrTable}

      <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
        {pcRows.length > 0 && (
          <Section title="PROTECTION CLASS" anchor="protectionClass" match={highlightMatch}>
            <MultTable leftHeader="Protection Class" rightHeader="Credit / Debit" rows={pcRows} headerBg={GREEN} highlightMatch={highlightMatch} />
            {basicRows.length > 0 && (
              <div style={{ marginTop: '8px' }}>
                <MultTable leftHeader="Cause of Loss" rightHeader="Credit / Debit" rows={basicRows} headerBg={GREEN} highlightMatch={highlightMatch} />
              </div>
            )}
          </Section>
        )}

        {ageRows.length > 0 && (
          <Section title="AGE OF BUILDING" anchor="AgeOfBuildingCredits" match={highlightMatch}>
            <MultTable leftHeader="Age of Building" rightHeader="Credit" rows={ageRows} headerBg={GREEN} highlightMatch={highlightMatch} />
          </Section>
        )}

        {aopRows.length > 0 && (
          <Section title="AOP DEDUCTIBLE" anchor="PropDeductible" match={highlightMatch}>
            <MultTable
              leftHeader="AOP Deductible"
              rightHeader="Credit"
              rows={aopRows}
              headerBg={GREEN}
              note="Minimum Deductible USD 1,000"
              highlightMatch={highlightMatch}
            />
            <div style={{ marginTop: '8px' }}>
              <MultTable
                leftHeader="Named Storm Deductible"
                rightHeader=""
                rows={[{ label: 'Named Windstorm', display: namedStorm, mbkey: 'WindDedOptionDebit|Named Windstorm' }]}
                headerBg={GREEN}
                highlightMatch={highlightMatch}
              />
            </div>
          </Section>
        )}
      </div>

      {privateRiskTable}
      {fireModSection}
      {windRateSection}
      {windDedSection}

      <div style={{ display: 'flex', gap: '30px', flexWrap: 'wrap' }}>
        {roofRows.length > 0 && (
          <Section title="ROOF REPLACED" anchor="Roofreplaced" match={highlightMatch}>
            <MultTable leftHeader="Roof Replaced" rightHeader="Credit" rows={roofRows} headerBg={BLUE} highlightMatch={highlightMatch} />
          </Section>
        )}

        {vacancyRows.length > 0 && (
          <Section title="BUILDING VACANCY" anchor="BuildingVacancy" match={highlightMatch}>
            <MultTable leftHeader="Building Vacancy" rightHeader="Debit" rows={vacancyRows} headerBg={BLUE} highlightMatch={highlightMatch} />
          </Section>
        )}

        {shutterRows.length > 0 && (
          <Section title="SHUTTERS" anchor="ShuttersCredit" match={highlightMatch}>
            <table style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle(BLUE)}>Zone</th>
                  <th style={thStyle(BLUE)}>Shutters</th>
                  <th style={thStyle(BLUE)}>Year Built</th>
                  <th style={thStyle(BLUE)}>Credit</th>
                </tr>
              </thead>
              <tbody>
                {shutterRows.map((r, i) => (
                  <tr key={i}>
                    <td style={tdStyle}>{r.zone}</td>
                    <td style={tdLeft}>{r.shutter}</td>
                    <td style={tdStyle}>{r.years}</td>
                    <td style={tdStyle}>{r.display}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Section>
        )}
      </div>

      {triaSection}

      <p style={{ marginTop: '10px', fontWeight: 'bold' }}>
        Rates to be at Coverholders Discretion where necessary
      </p>
    </div>
  );
};

export default MercantileBraceRateTables;
